import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import { Settings, Loader2, Save, Info } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from '@/hooks/useToastHistory'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'

interface PerplexityLLMSettings {
  id?: string
  client_id: string | null
  prompt_type: string
  system_prompt: string
  user_prompt_template: string
  model: string
  temperature: number
  max_tokens: number
  top_p: number
  frequency_penalty: number
  presence_penalty: number
  return_images: boolean
  return_related_questions: boolean
  stream: boolean
  json_mode: boolean
}

const PERPLEXITY_MODELS = [
  { value: 'sonar', label: 'Sonar (Lightweight Search)', description: 'Fast, efficient for most tasks' },
  { value: 'sonar-pro', label: 'Sonar Pro (Advanced Search)', description: 'Enhanced search capabilities' },
  { value: 'sonar-deep-research', label: 'Sonar Deep Research', description: 'Exhaustive research mode' },
  { value: 'sonar-reasoning', label: 'Sonar Reasoning', description: 'Fast reasoning tasks' },
  { value: 'sonar-reasoning-pro', label: 'Sonar Reasoning Pro', description: 'Premier reasoning' },
]

export function MasterInboxLLMSettingsDialog() {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<PerplexityLLMSettings>({
    client_id: null, // Global default
    prompt_type: 'ai_categorization',
    system_prompt: 'You are a precise email categorization assistant. Always respond with valid JSON only.',
    user_prompt_template: '',
    model: 'sonar',
    temperature: 0.1,
    max_tokens: 2000,
    top_p: 0.9,
    frequency_penalty: 0,
    presence_penalty: 0,
    return_images: false,
    return_related_questions: false,
    stream: false,
    json_mode: true,
  })

  useEffect(() => {
    if (open) {
      loadSettings()
    }
  }, [open])

  const loadSettings = async () => {
    setLoading(true)
    try {
      // Get the most recent settings (same logic as Edge Function)
      // Order by created_at descending and limit to 1 to get the latest
      const { data, error } = await supabase
        .from('perplexity_llm_settings')
        .select('*')
        .is('client_id', null) // Global default
        .eq('prompt_type', 'ai_categorization')
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = not found
        throw error
      }

      if (data) {
        setSettings(data as PerplexityLLMSettings)
      } else {
        // Use defaults if no settings found
        console.log('No settings found, using defaults')
      }
    } catch (err: any) {
      console.error('Error loading settings:', err)
      toast({
        title: 'Error',
        description: 'Failed to load LLM settings',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Exclude id, created_at, updated_at, created_by from upsert payload
      // These are managed by the database
      const { id, created_at, updated_at, created_by, ...settingsToSave } = settings
      
      const payload = {
        ...settingsToSave,
        client_id: null, // Always global
        prompt_type: 'ai_categorization',
      }

      // Upsert (insert or update) based on client_id and prompt_type
      const { error } = await supabase
        .from('perplexity_llm_settings')
        .upsert(payload, {
          onConflict: 'client_id,prompt_type',
        })

      if (error) throw error

      toast({
        title: 'Success',
        description: 'LLM settings saved successfully'
      })

      setOpen(false)
    } catch (err: any) {
      console.error('Error saving settings:', err)
      toast({
        title: 'Error',
        description: err.message || 'Failed to save settings',
        variant: 'destructive'
      })
    } finally {
      setSaving(false)
    }
  }

  const updateSetting = <K extends keyof PerplexityLLMSettings>(key: K, value: PerplexityLLMSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings className="w-4 h-4" />
          LLM Settings
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Master Inbox LLM Settings</DialogTitle>
          <DialogDescription>
            Configure Perplexity AI settings and prompts for Master Inbox reply categorization. These are global settings applied to all clients.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <TooltipProvider>
            <div className="space-y-6">
              <Accordion type="multiple" defaultValue={["prompts", "model"]} className="w-full">
                {/* Prompts Section */}
                <AccordionItem value="prompts">
                  <AccordionTrigger>Prompts</AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="system_prompt">System Prompt</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Instructions that define how the AI should behave. This is like giving the AI a personality or role to play.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Textarea
                        id="system_prompt"
                        value={settings.system_prompt}
                        onChange={(e) => updateSetting('system_prompt', e.target.value)}
                        placeholder="System message for Perplexity API..."
                        rows={4}
                        className="font-mono text-sm"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="user_prompt_template">User Prompt Template</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Template with placeholders: {'{original_subject}'}, {'{sent_message}'}, {'{reply_message}'}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Textarea
                        id="user_prompt_template"
                        value={settings.user_prompt_template}
                        onChange={(e) => updateSetting('user_prompt_template', e.target.value)}
                        placeholder="User prompt template with placeholders..."
                        rows={12}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        Available placeholders: {'{original_subject}'}, {'{sent_message}'}, {'{reply_message}'}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Model & Core Settings */}
                <AccordionItem value="model">
                  <AccordionTrigger>Model & Core Settings</AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="model">Model</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Choose the Perplexity model that will process categorization requests.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Select value={settings.model} onValueChange={(value) => updateSetting('model', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a model" />
                        </SelectTrigger>
                        <SelectContent>
                          {PERPLEXITY_MODELS.map((model) => (
                            <SelectItem key={model.value} value={model.value}>
                              <div>
                                <div className="font-medium">{model.label}</div>
                                <div className="text-xs text-muted-foreground">{model.description}</div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="temperature">Temperature</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>Controls creativity vs consistency. 0 = deterministic, 2 = very creative</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className="text-sm text-muted-foreground">{settings.temperature}</span>
                      </div>
                      <Slider
                        id="temperature"
                        min={0}
                        max={2}
                        step={0.1}
                        value={[settings.temperature]}
                        onValueChange={(value) => updateSetting('temperature', value[0])}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="max_tokens">Max Tokens</Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>Maximum length of AI response. Higher = longer but more expensive.</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Input
                        id="max_tokens"
                        type="number"
                        value={settings.max_tokens}
                        onChange={(e) => updateSetting('max_tokens', parseInt(e.target.value) || 2000)}
                        min={1}
                        max={32000}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="top_p">Top-p (Nucleus Sampling)</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>Alternative to temperature. 0.1 = very focused, 0.9 = diverse</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className="text-sm text-muted-foreground">{settings.top_p}</span>
                      </div>
                      <Slider
                        id="top_p"
                        min={0}
                        max={1}
                        step={0.05}
                        value={[settings.top_p]}
                        onValueChange={(value) => updateSetting('top_p', value[0])}
                        className="w-full"
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Advanced Settings */}
                <AccordionItem value="advanced">
                  <AccordionTrigger>Advanced Settings</AccordionTrigger>
                  <AccordionContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="frequency_penalty">Frequency Penalty</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>Prevents repeating same words too often. Positive = less repetitive</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className="text-sm text-muted-foreground">{settings.frequency_penalty}</span>
                      </div>
                      <Slider
                        id="frequency_penalty"
                        min={-2}
                        max={2}
                        step={0.1}
                        value={[settings.frequency_penalty]}
                        onValueChange={(value) => updateSetting('frequency_penalty', value[0])}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="presence_penalty">Presence Penalty</Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>Encourages new topics vs repeating ideas. Positive = more diverse topics</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className="text-sm text-muted-foreground">{settings.presence_penalty}</span>
                      </div>
                      <Slider
                        id="presence_penalty"
                        min={-2}
                        max={2}
                        step={0.1}
                        value={[settings.presence_penalty]}
                        onValueChange={(value) => updateSetting('presence_penalty', value[0])}
                        className="w-full"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="json_mode"
                        checked={settings.json_mode}
                        onCheckedChange={(checked) => updateSetting('json_mode', checked)}
                      />
                      <Label htmlFor="json_mode">JSON Mode (Structured Response)</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="return_images"
                        checked={settings.return_images}
                        onCheckedChange={(checked) => updateSetting('return_images', checked)}
                      />
                      <Label htmlFor="return_images">Return Images</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="return_related_questions"
                        checked={settings.return_related_questions}
                        onCheckedChange={(checked) => updateSetting('return_related_questions', checked)}
                      />
                      <Label htmlFor="return_related_questions">Return Related Questions</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="stream"
                        checked={settings.stream}
                        onCheckedChange={(checked) => updateSetting('stream', checked)}
                      />
                      <Label htmlFor="stream">Streaming Response</Label>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </TooltipProvider>
        )}

        <Separator />

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
